/**
 * Rendelések — the order list of handover rev7 §11.7, whose roles are "all".
 *
 * A list and a filter, and no action: everything that changes an order has its
 * own view with its own role, and an order list that could also approve things
 * would be a second review queue with no gate of its own.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { asDate, fmt, Loading, statusText, STATUS_HU, useConsoleData } from '../common.tsx'

interface OrderRow {
  id: string
  ref: string
  status: string
  status_reason: string | null
  lane: string
  event_date: string
  event_name: string | null
  guests: number
  customer_name: string
  customer_company: string | null
  gross: number
  is_late: boolean
  is_short_notice: boolean
  /** SimplePay test (20261103): a SimplePay test order. */
  is_test?: boolean
}

/** SimplePay test prompt §2: show or hide the test orders. Shown by default,
 *  each with its TESZT badge, so a tester's order can be found at once. */
type TestFilter = 'show' | 'hide' | 'only'

export function Orders(): JSX.Element {
  const [status, setStatus] = useState('')
  const [query, setQuery] = useState('')
  const [applied, setApplied] = useState({ status: '', query: '' })
  const [tests, setTests] = useState<TestFilter>('show')
  const { data, error, loading } = useConsoleData<{ orders: OrderRow[] }>('orders', {
    ...(applied.status === '' ? {} : { status: applied.status }),
    ...(applied.query === '' ? {} : { query: applied.query }),
  })

  return (
    <section data-testid="view-orders">
      <h2>Rendelések</h2>
      <p className="lead">§11.7: a rendelés részletes nézete minden konzolszerepkörnek látható.</p>

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          setApplied({ status, query })
        }}
      >
        <label htmlFor="order-status">Állapot</label>
        <select
          id="order-status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          data-testid="filter-status"
        >
          <option value="">Mind</option>
          {Object.entries(STATUS_HU).map(([key, label]) => (
            <option value={key} key={key}>{label}</option>
          ))}
        </select>
        <label htmlFor="order-query">Keresés (azonosító, e-mail, cég, név)</label>
        <input
          id="order-query"
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid="filter-query"
        />
        <label htmlFor="order-tests">Teszt rendelések</label>
        <select
          id="order-tests"
          value={tests}
          onChange={(event) => setTests(event.target.value as TestFilter)}
          data-testid="filter-tests"
        >
          <option value="show">Látszanak</option>
          <option value="hide">Elrejtve</option>
          <option value="only">Csak a teszt rendelések</option>
        </select>
        <div className="actions">
          <button type="submit" className="primary" data-testid="filter-apply">Szűrés</button>
        </div>
      </form>

      <Loading loading={loading} error={error} />

      {data !== null && data.orders.length === 0 && (
        <p className="note" data-testid="orders-empty">Nincs a szűrésnek megfelelő rendelés.</p>
      )}

      {data !== null && data.orders.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Rendelés</th>
              <th>Állapot</th>
              <th>Rendezvény</th>
              <th className="num">Vendég</th>
              <th>Vendég / cég</th>
              <th className="num">Bruttó</th>
            </tr>
          </thead>
          <tbody>
            {data.orders
              .filter((order) =>
                tests === 'show' ? true : tests === 'hide' ? order.is_test !== true : order.is_test === true,
              )
              .map((order) => (
              <tr key={order.id} data-testid={`order-${order.ref}`}>
                <td>
                  <Link to={`/staff/orders/${order.id}`}>{order.ref}</Link>
                  {order.is_test === true && (
                    <span className="tag stop" data-testid="tag-test">TESZT</span>
                  )}
                  {order.is_short_notice && <span className="tag warn">rövid határidő</span>}
                  {order.is_late && <span className="tag warn">késői</span>}
                </td>
                <td>{statusText(order.status, order.status_reason)}</td>
                <td>
                  {asDate(order.event_date, true)}
                  {order.event_name !== null && <div className="tag">{order.event_name}</div>}
                </td>
                <td className="num">{fmt.n(order.guests)}</td>
                <td>{order.customer_company ?? order.customer_name}</td>
                <td className="num">{fmt.money(order.gross)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
